import React, { useEffect, useRef, useState, useCallback } from 'react';
import './DesiBitesDash.css';

// ── Canvas logical dimensions ─────────────────────────────────────────────────
const W = 480;
const H = 640;

// ── Detect mobile once ────────────────────────────────────────────────────────
const IS_MOBILE = typeof window !== 'undefined' &&
  (window.matchMedia('(pointer: coarse)').matches || window.innerWidth < 600);

// ── Plate ─────────────────────────────────────────────────────────────────────
const PLATE_W            = 88;
const PLATE_H            = 16;
const PLATE_BOTTOM_OFFSET = 56;
const PLATE_SPEED        = IS_MOBILE ? 10 : 7;   // faster on mobile

// ── Items ─────────────────────────────────────────────────────────────────────
const ITEM_R   = 22;
const LANE_CNT = 7;

// Base speed tuned for 60fps; delta-time normalises to any frame rate
const BASE_SPEED = IS_MOBILE ? 2.6 : 2.0;

const FOOD_EMOJIS = [
  { e: '\u{1F35B}', pts: 10 }, { e: '\u{1F958}', pts: 15 }, { e: '\u{1F95F}', pts: 10 },
  { e: '\u{1F9C6}', pts: 12 }, { e: '\u{1F959}', pts: 10 }, { e: '\u{1F35C}', pts: 8  },
  { e: '\u{1F35A}', pts: 8  }, { e: '\u{1F957}', pts: 8  }, { e: '\u{1FAD5}', pts: 12 },
  { e: '\u{1F362}', pts: 10 }, { e: '\u{1F9CB}', pts: 10 }, { e: '\u{1FAD3}', pts: 8  },
];
const BAD_EMOJIS   = ['\u{1FAB0}', '\u{1F41B}', '\u{1F480}'];
const BONUS_EMOJIS = [
  { e: '\u2B50', pts: 50 }, { e: '\u{1F336}\uFE0F', pts: 30 },
  { e: '\u{1F48E}', pts: 100 }, { e: '\u{1F3C6}', pts: 75 },
];

function rand(arr) { return arr[Math.floor(Math.random() * arr.length)]; }

function spawnItem(level) {
  const lane  = Math.floor(Math.random() * LANE_CNT);
  const x     = (lane + 0.5) * (W / LANE_CNT);
  const speed = BASE_SPEED + level * 0.45 + Math.random() * 1.2;

  const roll = Math.random();
  if (roll < 0.10) {
    return { id: Math.random(), x, y: -ITEM_R * 2, speed, type: 'bad',   e: rand(BAD_EMOJIS),   pts: 0 };
  } else if (roll < 0.18) {
    const b = rand(BONUS_EMOJIS);
    return { id: Math.random(), x, y: -ITEM_R * 2, speed: speed * 0.82, type: 'bonus', e: b.e, pts: b.pts };
  } else {
    const f = rand(FOOD_EMOJIS);
    return { id: Math.random(), x, y: -ITEM_R * 2, speed, type: 'food', e: f.e, pts: f.pts };
  }
}

const PARTICLE_COUNT = IS_MOBILE ? 4 : 7;
function spawnParticles(x, y, e, count = PARTICLE_COUNT) {
  return Array.from({ length: count }, () => ({
    id: Math.random(), x, y,
    vx: (Math.random() - 0.5) * 7,
    vy: -(Math.random() * 5 + 1.5),
    e, life: 1,
  }));
}

function mkPopup(x, y, text, color) {
  return { id: Math.random(), x, y, text, color, alpha: 1, vy: -1.2 };
}

function initState() {
  return {
    plateX: W / 2,
    items: [], particles: [], popups: [],
    score: 0, lives: 3, level: 1, catchCount: 0,
    spawnTimer: 0,
    spawnIntervalMs: IS_MOBILE ? 1100 : 1367,
    keys: { left: false, right: false },
    mouseX: W / 2,
    shake: 0,
    _stars: null, _bgGrad: null, _vignette: null,
  };
}

// ── Main component ────────────────────────────────────────────────────────────
export default function DesiBitesDash() {
  const canvasRef   = useRef(null);
  const gRef        = useRef(null);
  const rafRef      = useRef(null);
  const lastTimeRef = useRef(null);
  const dprRef      = useRef(1);

  const [screen,  setScreen]  = useState('start');
  const [hud,     setHud]     = useState({ score: 0, lives: 3, level: 1 });
  const [hiScore, setHiScore] = useState(() => +(localStorage.getItem('dbd_hi') || '0'));

  // ── Canvas DPR setup ─────────────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    dprRef.current = dpr;
    canvas.width  = W * dpr;
    canvas.height = H * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
  }, []);

  const initGame = useCallback(() => {
    gRef.current = initState();
    lastTimeRef.current = null;
  }, []);

  // ── Draw ─────────────────────────────────────────────────────────────────
  const draw = useCallback((ctx, g) => {
    ctx.save();
    if (g.shake > 0) {
      const s = g.shake * 3;
      ctx.translate((Math.random() - .5) * s, (Math.random() - .5) * s);
    }

    if (!g._bgGrad) {
      const bg = ctx.createLinearGradient(0, 0, 0, H);
      bg.addColorStop(0,   '#0d0b1e');
      bg.addColorStop(.55, '#1e1b4b');
      bg.addColorStop(1,   '#0d0b1e');
      g._bgGrad = bg;
    }
    ctx.fillStyle = g._bgGrad;
    ctx.fillRect(0, 0, W, H);

    if (!g._stars) {
      g._stars = Array.from({ length: IS_MOBILE ? 40 : 70 }, () => ({
        x: Math.random() * W, y: Math.random() * H,
        r: Math.random() * 1.5 + 0.4,
        phase: Math.random() * Math.PI * 2,
        speed: Math.random() * 0.002 + 0.001,
      }));
    }
    const nowS = performance.now();
    for (const s of g._stars) {
      const alpha = 0.45 + Math.sin(nowS * s.speed + s.phase) * 0.25;
      ctx.fillStyle = `rgba(255,255,255,${alpha.toFixed(2)})`;
      ctx.beginPath();
      ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
      ctx.fill();
    }

    // vignette — darken edges so items glow pop
    if (!g._vignette) {
      const vg = ctx.createRadialGradient(W/2, H/2, H*0.28, W/2, H/2, H*0.82);
      vg.addColorStop(0, 'rgba(0,0,0,0)');
      vg.addColorStop(1, 'rgba(0,0,0,0.58)');
      g._vignette = vg;
    }
    ctx.fillStyle = g._vignette;
    ctx.fillRect(0, 0, W, H);

    ctx.setLineDash([6, 14]);
    ctx.strokeStyle = 'rgba(255,255,255,0.05)';
    ctx.lineWidth = 1;
    for (let i = 1; i < LANE_CNT; i++) {
      const lx = i * W / LANE_CNT;
      ctx.beginPath(); ctx.moveTo(lx, 0); ctx.lineTo(lx, H); ctx.stroke();
    }
    ctx.setLineDash([]);

    // ── Draw items with glow halos ──────────────────────────────────────────
    const now = performance.now();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    for (const item of g.items) {
      ctx.save();

      // per-type palette
      let glowColor, haloInner, haloOuter, glowBlur;
      if (item.type === 'bonus') {
        glowColor = '#fbbf24'; haloInner = 'rgba(251,191,36,0.38)'; haloOuter = 'rgba(251,191,36,0)'; glowBlur = IS_MOBILE ? 10 : 26;
      } else if (item.type === 'bad') {
        glowColor = '#ef4444'; haloInner = 'rgba(239,68,68,0.42)';  haloOuter = 'rgba(239,68,68,0)';  glowBlur = IS_MOBILE ? 8  : 20;
      } else {
        glowColor = '#34d399'; haloInner = 'rgba(52,211,153,0.28)'; haloOuter = 'rgba(52,211,153,0)'; glowBlur = IS_MOBILE ? 6  : 14;
      }

      // pulsing scale for bonus items
      let scale = 1;
      if (item.type === 'bonus') {
        scale = 1 + Math.sin(now * 0.004 + item.id * 10) * 0.08;
      }
      ctx.translate(item.x, item.y);
      ctx.scale(scale, scale);

      // radial halo circle
      const haloR = ITEM_R * (IS_MOBILE ? 1.55 : 1.75);
      const grad = ctx.createRadialGradient(0, 0, ITEM_R * 0.3, 0, 0, haloR);
      grad.addColorStop(0, haloInner);
      grad.addColorStop(1, haloOuter);
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(0, 0, haloR, 0, Math.PI * 2);
      ctx.fill();

      // shadow glow behind emoji
      ctx.shadowColor = glowColor;
      ctx.shadowBlur  = glowBlur;

      // emoji itself — larger font
      ctx.font = `${ITEM_R * 2.1}px serif`;
      ctx.fillText(item.e, 0, 0);

      ctx.restore();
    }

    ctx.font = '18px serif';
    for (const p of g.particles) {
      ctx.save();
      ctx.globalAlpha = Math.max(0, p.life);
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText(p.e, p.x, p.y);
      ctx.restore();
    }

    const plateY = H - PLATE_BOTTOM_OFFSET;
    const px = g.plateX;
    ctx.save();
    if (!IS_MOBILE) { ctx.shadowColor = '#d97706'; ctx.shadowBlur = 16; }
    const pg = ctx.createLinearGradient(px - PLATE_W / 2, plateY, px + PLATE_W / 2, plateY + PLATE_H);
    pg.addColorStop(0, '#fbbf24'); pg.addColorStop(.5, '#d97706'); pg.addColorStop(1, '#b45309');
    ctx.fillStyle = pg;
    ctx.beginPath();
    ctx.roundRect(px - PLATE_W / 2, plateY, PLATE_W, PLATE_H, 9);
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.fillStyle = 'rgba(255,255,255,0.28)';
    ctx.beginPath();
    ctx.roundRect(px - PLATE_W / 2 + 10, plateY + 3, PLATE_W - 20, 5, 3);
    ctx.fill();
    ctx.restore();

    ctx.font = 'bold 16px -apple-system, sans-serif';
    for (const p of g.popups) {
      ctx.save();
      ctx.globalAlpha = Math.max(0, p.alpha);
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillStyle = p.color;
      if (!IS_MOBILE) { ctx.shadowColor = p.color; ctx.shadowBlur = 8; }
      ctx.fillText(p.text, p.x, p.y);
      ctx.restore();
    }

    ctx.restore();
  }, []);

  // ── Game loop (delta-time normalised to 60fps) ────────────────────────────
  const loop = useCallback((timestamp) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const g   = gRef.current;
    if (!g) return;

    const last = lastTimeRef.current || timestamp;
    const dt   = Math.min(timestamp - last, 50);
    lastTimeRef.current = timestamp;
    const t = dt / 16.667;  // 1.0 = 60fps, 2.0 = 30fps

    if (g.keys.left)  g.plateX = Math.max(PLATE_W / 2, g.plateX - PLATE_SPEED * t);
    if (g.keys.right) g.plateX = Math.min(W - PLATE_W / 2, g.plateX + PLATE_SPEED * t);
    if (!g.keys.left && !g.keys.right) {
      const lf = IS_MOBILE ? 0.45 : 0.18;
      const k  = 1 - Math.pow(1 - lf, t);
      g.plateX += (g.mouseX - g.plateX) * k;
      g.plateX = Math.max(PLATE_W / 2, Math.min(W - PLATE_W / 2, g.plateX));
    }

    g.spawnTimer += dt;
    if (g.spawnTimer >= g.spawnIntervalMs) {
      g.spawnTimer = 0;
      g.items.push(spawnItem(g.level));
      if (g.level >= 4 && Math.random() < 0.28) g.items.push(spawnItem(g.level));
      if (g.level >= 7 && Math.random() < 0.18) g.items.push(spawnItem(g.level));
    }

    const py   = H - PLATE_BOTTOM_OFFSET;
    const dead = new Set();
    let scoreChanged = false;

    for (const item of g.items) {
      item.y += item.speed * t;
      if (
        item.y + ITEM_R > py && item.y - ITEM_R < py + PLATE_H &&
        item.x > g.plateX - PLATE_W / 2 - ITEM_R &&
        item.x < g.plateX + PLATE_W / 2 + ITEM_R
      ) {
        dead.add(item.id);
        if (item.type === 'bad') {
          g.lives = Math.max(0, g.lives - 1);
          g.shake = 8;
          g.particles.push(...spawnParticles(item.x, item.y, '\u{1F4A5}', IS_MOBILE ? 3 : 5));
          g.popups.push(mkPopup(item.x, py - 24, '\u2212LIFE!', '#f87171'));
          scoreChanged = true;
          if (g.lives === 0) {
            cancelAnimationFrame(rafRef.current);
            const hi = Math.max(g.score, hiScore);
            localStorage.setItem('dbd_hi', hi);
            setHiScore(hi);
            setHud({ score: g.score, lives: 0, level: g.level });
            setScreen('gameover');
            return;
          }
        } else {
          g.score     += item.pts;
          g.catchCount++;
          if (g.catchCount % 12 === 0) {
            g.level = Math.min(12, g.level + 1);
            const minMs = IS_MOBILE ? 360 : 467;
            g.spawnIntervalMs = Math.max(minMs, g.spawnIntervalMs - (IS_MOBILE ? 55 : 67));
            g.popups.push(mkPopup(W / 2, py - 50, `\u2B06 LEVEL ${g.level}!`, '#fbbf24'));
          }
          g.particles.push(...spawnParticles(item.x, item.y, item.e));
          const label = item.type === 'bonus' ? `+${item.pts}!!` : `+${item.pts}`;
          const color = item.type === 'bonus' ? '#fbbf24' : '#34d399';
          g.popups.push(mkPopup(item.x, py - 24, label, color));
          scoreChanged = true;
        }
      }
      if (item.y > H + ITEM_R * 2) dead.add(item.id);
    }
    g.items = g.items.filter(it => !dead.has(it.id));

    for (const p of g.particles) {
      p.x += p.vx * t; p.y += p.vy * t; p.vy += 0.18 * t; p.life -= 0.035 * t;
    }
    g.particles = g.particles.filter(p => p.life > 0);

    for (const p of g.popups) { p.y += p.vy * t; p.alpha -= 0.025 * t; }
    g.popups = g.popups.filter(p => p.alpha > 0);

    if (g.shake > 0) g.shake = Math.max(0, g.shake - 0.7 * t);
    if (scoreChanged) setHud({ score: g.score, lives: g.lives, level: g.level });

    draw(ctx, g);
    rafRef.current = requestAnimationFrame(loop);
  }, [draw, hiScore]);

  const startGame = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    initGame();
    setHud({ score: 0, lives: 3, level: 1 });
    setScreen('playing');
  }, [initGame]);

  const resumeGame = useCallback(() => {
    lastTimeRef.current = null;
    setScreen('playing');
  }, []);

  useEffect(() => {
    if (screen === 'playing') {
      lastTimeRef.current = null;
      rafRef.current = requestAnimationFrame(loop);
    } else {
      cancelAnimationFrame(rafRef.current);
    }
    return () => cancelAnimationFrame(rafRef.current);
  }, [screen, loop]);

  useEffect(() => {
    const down = (e) => {
      const g = gRef.current; if (!g) return;
      if (e.key === 'ArrowLeft'  || e.key === 'a') { g.keys.left  = true; e.preventDefault(); }
      if (e.key === 'ArrowRight' || e.key === 'd') { g.keys.right = true; e.preventDefault(); }
      if (e.key === 'Escape' && screen === 'playing') setScreen('paused');
      if ((e.key === ' ' || e.key === 'p') && screen === 'playing') setScreen('paused');
      if ((e.key === ' ' || e.key === 'p') && screen === 'paused') { lastTimeRef.current = null; setScreen('playing'); }
    };
    const up = (e) => {
      const g = gRef.current; if (!g) return;
      if (e.key === 'ArrowLeft'  || e.key === 'a') g.keys.left  = false;
      if (e.key === 'ArrowRight' || e.key === 'd') g.keys.right = false;
    };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup',   up);
    return () => { window.removeEventListener('keydown', down); window.removeEventListener('keyup', up); };
  }, [screen]);

  const onPointerMove = useCallback((e) => {
    const g = gRef.current; const canvas = canvasRef.current;
    if (!g || !canvas || e.pointerType === 'touch') return;
    const rect = canvas.getBoundingClientRect();
    g.mouseX = (e.clientX - rect.left) * (W / rect.width);
  }, []);

  const onTouchMove = useCallback((e) => {
    const g = gRef.current; const canvas = canvasRef.current;
    if (!g || !canvas) return;
    e.preventDefault();
    const rect   = canvas.getBoundingClientRect();
    const touchX = (e.touches[0].clientX - rect.left) * (W / rect.width);
    g.mouseX  = touchX;
    g.plateX  = Math.max(PLATE_W / 2, Math.min(W - PLATE_W / 2, touchX));
  }, []);

  const hearts = Array.from({ length: 3 }, (_, i) => i < hud.lives ? '\u2764\uFE0F' : '\u{1F5A4}');

  return (
    <div className="dbd">
      <div className="dbd__page-header">
        <div className="dbd__page-title">🎮 Desi Bites Dash</div>
        <div className="dbd__page-sub">Catch the falling desi food • Dodge the bugs • Beat your best!</div>
      </div>

      <div className="dbd__layout">
        <div className="dbd__game-col">
          {screen === 'playing' && (
            <div className="dbd__hud">
              <div className="dbd__hud-score">⭐ {hud.score}</div>
              <div className="dbd__hud-level">Lv {hud.level}</div>
              <div className="dbd__hud-lives">{hearts.join(' ')}</div>
              <button className="dbd__hud-pause" onClick={() => setScreen('paused')}>⏸</button>
            </div>
          )}

          <div className="dbd__canvas-wrap">
            <canvas
              ref={canvasRef}
              className="dbd__canvas"
              onPointerMove={onPointerMove}
              onTouchMove={onTouchMove}
              onTouchStart={onTouchMove}
            />

            {screen === 'start' && (
              <div className="dbd__overlay">
                <div className="dbd__card">
                  <div className="dbd__big-emoji">🍛</div>
                  <h1 className="dbd__card-title">Desi Bites<br /><span className="dbd__title-accent">Dash</span></h1>
                  <p className="dbd__card-sub">Catch the falling desi food with your plate.<br />Avoid the bugs or lose a life!</p>
                  <div className="dbd__legend">
                    <div className="dbd__legend-item"><span>🍛</span><span>Food = +pts</span></div>
                    <div className="dbd__legend-item"><span>⭐</span><span>Star = +50</span></div>
                    <div className="dbd__legend-item"><span>💎</span><span>Diamond = +100</span></div>
                    <div className="dbd__legend-item"><span>🪰</span><span>Bug = −LIFE!</span></div>
                  </div>
                  {hiScore > 0 && <div className="dbd__hi-badge">🏆 Best: {hiScore}</div>}
                  <button className="dbd__btn dbd__btn--primary" onClick={startGame}>🎮 Play Now</button>
                  <div className="dbd__controls-hint">
                    {IS_MOBILE
                      ? <span>Drag canvas or tap ◄ ► buttons</span>
                      : <><span>← → or A / D keys</span><span>•</span><span>Mouse</span></>}
                  </div>
                </div>
              </div>
            )}

            {screen === 'paused' && (
              <div className="dbd__overlay">
                <div className="dbd__card">
                  <div className="dbd__big-emoji">⏸️</div>
                  <h2 className="dbd__card-title" style={{ fontSize: '1.9rem' }}>Paused</h2>
                  <div className="dbd__pause-stats">
                    <div><span>Score</span><strong>{hud.score}</strong></div>
                    <div><span>Level</span><strong>{hud.level}</strong></div>
                    <div><span>Lives</span><strong>{hud.lives} / 3</strong></div>
                  </div>
                  <button className="dbd__btn dbd__btn--primary" onClick={resumeGame}>▶ Resume</button>
                  <button className="dbd__btn dbd__btn--ghost"   onClick={startGame}>↩ Restart</button>
                </div>
              </div>
            )}

            {screen === 'gameover' && (
              <div className="dbd__overlay">
                <div className="dbd__card">
                  <div className="dbd__big-emoji" style={{ animation: 'none', fontSize: '2.8rem' }}>💀</div>
                  <h2 className="dbd__card-title" style={{ fontSize: '1.8rem' }}>Game Over!</h2>
                  <div className="dbd__result-grid">
                    <div className="dbd__result-row"><span>Your Score</span><strong className="dbd__result-score">{hud.score}</strong></div>
                    <div className="dbd__result-row"><span>Level Reached</span><strong>{hud.level}</strong></div>
                    <div className="dbd__result-row dbd__result-row--hi"><span>🏆 Best Score</span><strong>{hiScore}</strong></div>
                  </div>
                  {hud.score > 0 && hud.score >= hiScore && <div className="dbd__newhi">🎉 New High Score!</div>}
                  <button className="dbd__btn dbd__btn--primary" onClick={startGame}>🔄 Play Again</button>
                  <button className="dbd__btn dbd__btn--ghost"   onClick={() => setScreen('start')}>🏠 Main Menu</button>
                </div>
              </div>
            )}
          </div>

          {screen === 'playing' && (
            <div className="dbd__touch-row">
              <button
                className="dbd__touch-btn"
                onPointerDown={(e) => { e.preventDefault(); gRef.current && (gRef.current.keys.left = true); }}
                onPointerUp={(e)   => { e.preventDefault(); gRef.current && (gRef.current.keys.left = false); }}
                onPointerCancel={() => gRef.current && (gRef.current.keys.left = false)}
                onPointerLeave={() => gRef.current && (gRef.current.keys.left = false)}
              >◄</button>
              <button className="dbd__touch-btn dbd__touch-btn--sm" onClick={() => setScreen('paused')}>⏸</button>
              <button
                className="dbd__touch-btn"
                onPointerDown={(e) => { e.preventDefault(); gRef.current && (gRef.current.keys.right = true); }}
                onPointerUp={(e)   => { e.preventDefault(); gRef.current && (gRef.current.keys.right = false); }}
                onPointerCancel={() => gRef.current && (gRef.current.keys.right = false)}
                onPointerLeave={() => gRef.current && (gRef.current.keys.right = false)}
              >►</button>
            </div>
          )}
        </div>

        <div className="dbd__panel">
          <div className="dbd__panel-block">
            <div className="dbd__panel-heading">HOW TO PLAY</div>
            <ul className="dbd__rules">
              <li>🕹️ {IS_MOBILE ? 'Drag canvas or tap ◄ ► buttons' : 'Use ← → / A D keys or mouse'}</li>
              <li>🍛 Catch falling Indian food to score</li>
              <li>⭐ Bonus items are worth way more!</li>
              <li>🪰 Avoid bugs — each one costs a life</li>
              <li>📈 Every 12 catches = Level Up (faster!)</li>
              <li>❤️ 3 lives total — make them count!</li>
            </ul>
          </div>
          <div className="dbd__panel-block">
            <div className="dbd__panel-heading">SCORING</div>
            <div className="dbd__score-table">
              {[
                ['\u{1F35B}', 'Curry',   '+10',  false],
                ['\u{1F958}', 'Biryani', '+15',  false],
                ['\u{1F95F}', 'Samosa',  '+10',  false],
                ['\u{1F336}\uFE0F','Chili','+30', true ],
                ['\u2B50',   'Star',    '+50',  true ],
                ['🏆','Trophy',  '+75',  true ],
                ['💎','Diamond', '+100', true ],
                ['🪰','Bug',     '−LIFE', false],
              ].map(([e, l, p, bonus]) => (
                <div key={l} className={`dbd__st-row${bonus ? ' dbd__st-row--bonus' : ''}`}>
                  <span className="dbd__st-emoji">{e}</span>
                  <span className="dbd__st-label">{l}</span>
                  <span className={`dbd__st-pts${p === '\u2212LIFE' ? ' dbd__st-pts--bad' : ''}`}>{p}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="dbd__panel-block dbd__panel-block--hi">
            <div className="dbd__panel-heading">🏆 HIGH SCORE</div>
            <div className="dbd__hi-score">{hiScore}</div>
            {hiScore === 0 && <div className="dbd__hi-empty">Be the first legend!</div>}
          </div>
          <div className="dbd__panel-block dbd__panel-block--tip">
            <div className="dbd__panel-heading">💡 PRO TIP</div>
            <p className="dbd__tip-text">Bonus items fall slower — plan ahead! Diamonds are rare but worth 100 pts. At Level 5+, two items spawn at once. Stay sharp! 🎯</p>
          </div>
        </div>
      </div>
    </div>
  );
}
